import React, { useMemo } from 'react'

import {
  usePRFileAllFrames,
  useIsCameraMoving,
  useRootNodeAbsoluteExtent,
  PRMarinaLayerType,
  usePRFileSections,
  convertEmbindVectorToArray,
} from '@sketch-hq/sketch-web-renderer'

import {
  EntityOverlay,
  getSectionOverlayEntities,
  hasFrameAncestor,
  isUnstyledSection,
  OverlayEntity,
  useEntityOverlayProps,
  EntityInteractions,
  EntityOverlayMode,
  toEntityOverlayMode,
  useCanvasInteractions,
} from '@sketch/canvas'
import { useInspectorContext } from '../../../FrameView'
import {
  useEntityOverlaysState,
  useEntityKeyboardShortcuts,
} from '../../../../canvas/hooks'
import { Rect, isVariantSetNode } from '@sketch/inspector'
import { useCanvasAnnotations } from '../../../../annotations/hooks'
type PageCanvasFrameOverlayProps = {
  entity: OverlayEntity
  mode: EntityOverlayMode
  framesRefs: React.RefObject<Record<string, HTMLDivElement | null>>
  isSelected: boolean
  isHovered: boolean
  interactions: EntityInteractions
  rootExtent: Rect
  // In the single-Section focus view, render the overlay even when the entity's
  // layer is hidden (isEnabled() === false), so a focused Section's children
  // stay navigable regardless of their design-time visibility.
  forceShow?: boolean
}

const PageCanvasFrameOverlay = React.memo(function PageCanvasFrameOverlay({
  entity,
  mode,
  framesRefs,
  isSelected,
  isHovered,
  interactions,
  rootExtent,
  forceShow,
}: PageCanvasFrameOverlayProps) {
  const node = entity.getNode()

  const isHidden = node?.isEnabled() === false
  const isSection = node?.isSection()

  // A section's `bounds` field reflects the extent including any overflowing
  // children, which can diverge from the section's own frame as shown in the
  // Mac app. getAbsoluteLayerBounds() reports the section's own frame instead.
  const bounds =
    isSection && node ? node.getAbsoluteLayerBounds() : entity.bounds

  const overlayProps = useEntityOverlayProps(entity, {
    mode,
    isSelected,
    isHovered,
    interactions,
  })

  // Hidden entities don't render overlay decorations unless selected - or unless
  // explicitly forced (a focused Section's children must stay overlaid even when
  // their layer is hidden, so they remain navigable).
  if (isHidden && !isSelected && !forceShow) {
    return null
  }

  // A variant set is a Section, not a component - it gets the component accent
  // colour of its own accord rather than by being counted as one.
  const isVariantSet = isVariantSetNode(node)

  const isComponent =
    node?.getLayer()?.type.value === PRMarinaLayerType.SymbolMaster

  const variantProperties = node
    ? convertEmbindVectorToArray(node.getVariantConfiguration()).map(
        ({ propertyName, valueName }) => ({ propertyName, valueName })
      )
    : undefined

  return (
    <EntityOverlay
      ref={(ref: HTMLDivElement | null) => {
        if (framesRefs.current) {
          framesRefs.current[entity.objectId] = ref
        }
      }}
      bounds={bounds}
      artboardName={entity.name}
      artboardUUID={entity.objectId}
      isComponent={isComponent}
      isVariantSet={isVariantSet}
      variantProperties={variantProperties}
      // A Section with no fill or border of its own is invisible on the canvas -
      // the dashed outline stands in for it, and steps aside as soon as the
      // Section gets a style that paints its own boundary.
      showDashedOutline={isUnstyledSection(node)}
      rootExtent={rootExtent}
      {...overlayProps}
    />
  )
})

/**
 * The overlay we show on top ot the page canvas top level frames. The overlay is used to show
 * the artboards' names, cta, outline and listen for interactions on them.
 * Note: we use a unified component for both view modes to use only one React tree and
 * avoid remounting all overlays when switching modes.
 */
export const PageCanvasFramesOverlays = React.memo(
  function PageCanvasFramesOverlays({
    focusedSectionUUID,
  }: {
    focusedSectionUUID?: string
  }) {
    const { isInspectorActive } = useInspectorContext()
    const fileFrames = usePRFileAllFrames()
    const fileSections = usePRFileSections()
    const sections = fileSections ?? []

    // Every frame on the page, however deeply it sits inside a Section - the
    // top-level-only list leaves those out when the file's root doesn't resolve
    // as a page, as a Preview's presentation file doesn't, and they would go
    // unoverlaid. Frames nested inside another frame are that frame's content
    // rather than entities of their own, so they stay out.
    const pageFrames = useMemo(
      () =>
        (fileFrames ?? []).filter(frame => !hasFrameAncestor(frame.getNode())),
      [fileFrames]
    )

    const isFocusingSection = focusedSectionUUID !== undefined

    // In the single-Section focus view, the overlays come from the focused
    // Section's own subtree: its direct child Sections to drill into, plus every
    // frame it contains at any depth. On the normal page canvas the PRFile's
    // top-level entities are used instead.
    // Memoized: this walks the Section's subtree, and the component re-renders
    // on every camera move to reposition the overlays.
    const focusedSectionEntities = useMemo(
      () =>
        isFocusingSection
          ? getSectionOverlayEntities(
              fileSections
                ?.find(section => section.objectId === focusedSectionUUID)
                ?.getNode() ?? null
            )
          : [],
      [isFocusingSection, focusedSectionUUID, fileSections]
    )

    // Paint larger (outer) sections first so nested (smaller) sections land on
    // top and stay clickable - otherwise a parent section's overlay would cover
    // its children and you couldn't navigate into them from the page canvas.
    const byContainment = (a: OverlayEntity, b: OverlayEntity) =>
      b.bounds.width * b.bounds.height - a.bounds.width * a.bounds.height

    const decoratedEntities: OverlayEntity[] = isFocusingSection
      ? [...focusedSectionEntities].sort(byContainment)
      : [...[...sections].sort(byContainment), ...pageFrames]

    // Resolved once for the whole list rather than per entity - see
    // `useEntityOverlaysState`.
    const {
      framesRefs,
      selectedEntityUUID,
      clearSelection,
      hoveredEntityUUID,
      interactions,
    } = useEntityOverlaysState()

    const rootExtent = useRootNodeAbsoluteExtent()
    // The renderer reports this as nullable before it is ready.
    const isCameraMoving = useIsCameraMoving() ?? false
    const { isPlacingDraftAnnotation } = useCanvasAnnotations()
    const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

    const mode = toEntityOverlayMode({
      isCanvasInteractionsEnabled,
      isInspectorActive,
      isPlacingDraftAnnotation: Boolean(isPlacingDraftAnnotation),
    })

    useEntityKeyboardShortcuts({
      mode,
      selectedEntityUUID,
      clearSelection,
      framesRefs,
      isCameraMoving,
    })

    return (
      <>
        {decoratedEntities.map(entity => (
          <PageCanvasFrameOverlay
            key={entity.objectId}
            entity={entity}
            mode={mode}
            framesRefs={framesRefs}
            isSelected={selectedEntityUUID === entity.objectId}
            isHovered={hoveredEntityUUID === entity.objectId}
            interactions={interactions}
            rootExtent={rootExtent}
            forceShow={isFocusingSection}
          />
        ))}
      </>
    )
  }
)
